import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { SessionService, SessionResponse } from '../services/session.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-auth-session',
  templateUrl: './auth-session.component.html',
  styleUrls: ['./auth-session.component.scss']
})
export class AuthSessionComponent implements OnInit {
  /**
   * Loading state for the component
   */
  loading = true;

  /**
   * Error message if session verification fails
   */
  errorMessage = '';

  /**
   * Constructor for AuthSessionComponent
   * @param route ActivatedRoute for accessing route parameters
   * @param router Router for navigation
   * @param sessionService Service for session verification
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sessionService: SessionService,
    private translate: TranslateService
  ) { }

  /**
   * Initialize component
   * Extract sessionId from route params and verify it with backend
   */
  ngOnInit(): void {
    // Get session ID from route parameters
    const sessionId = this.route.snapshot.paramMap.get('sessionId');

    if (!sessionId) {
      this.handleError('Session ID not provided');
      return;
    }

    // Get query parameters to preserve them after session verification
    const queryParams = this.route.snapshot.queryParams;

    // Verify session with backend
    this.verifySession(sessionId, queryParams);
  }

  /**
   * Handle session verification with backend
   * @param sessionId The session ID to verify
   * @param queryParams Query parameters to preserve during navigation
   */
  private verifySession(sessionId: string, queryParams: any): void {
    this.sessionService.verifySession(sessionId)
      .pipe(
        catchError(error => {
          console.error('Session verification error:', error);
          return of({
            valid: false,
            user: null,
            submissionId: null,
            token: null
          } as SessionResponse);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((response: SessionResponse) => {
        if (response.valid) {
          // Session verification successful, navigate to report with preserved query params
          this.sessionService.navigateToReport(response, queryParams);
        } else {
          // Session verification failed
          this.handleError('Invalid or expired session');
        }
      });
  }

  /**
   * Handle error cases
   * @param message Error message to display
   */
  private handleError(message: string): void {
    this.errorMessage = message;
    this.loading = false;
  }
}
